// admin/VerificationQueue.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  watchPendingIdVerifications,
  watchPendingRightToList,
  reviewIdVerification,
  reviewRightToList,
} from "../firebase";

export default function VerificationQueue() {
  const [tab, setTab] = useState("id"); // 'id' | 'rtl'
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    let off;
    if (tab === "id") {
      off = watchPendingIdVerifications({
        onChange: (xs) => {
          setRows(xs || []);
          setLoading(false);
        },
        onError: (e) => {
          setError("Failed to load ID verifications.");
          setLoading(false);
        },
      });
    } else {
      off = watchPendingRightToList({
        onChange: (xs) => {
          setRows(xs || []);
          setLoading(false);
        },
        onError: (e) => {
          setError("Failed to load Right-to-List verifications.");
          setLoading(false);
        },
      });
    }
    return () => off && off();
  }, [tab]);

  const title = tab === "id" ? "ID + Selfie" : "Right to List";

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Verification Queue</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Review {title} documents submitted by hosts.
        </p>
      </header>

      <div className="flex gap-2">
        <TabButton active={tab === "id"} onClick={() => setTab("id")}>
          ID + Selfie
        </TabButton>
        <TabButton active={tab === "rtl"} onClick={() => setTab("rtl")}>
          Right to List
        </TabButton>
      </div>

      <section className="rounded-2xl border border-neutral-200 p-6">
        {loading ? (
          <div className="animate-pulse text-sm text-neutral-500">Loading…</div>
        ) : error ? (
          <div className="text-sm text-red-600">{error}</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-neutral-600">No pending verifications 🎉</div>
        ) : (
          <div className="grid gap-4">
            {rows.map((r) => {
              const submittedAt =
                r?.submittedAt ||
                r?.idVerification?.submittedAt ||
                r?.rightToList?.submittedAt ||
                null;

              const { thumbs, labels } =
                tab === "id"
                  ? mapIdThumbs(r)
                  : mapRtlThumbs(r);

              return (
                <Card key={r.uid}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold">Host: {r.uid}</div>
                      <div className="text-xs text-neutral-500">
                        {tab === "id"
                          ? "ID + Selfie"
                          : `Right to List${r?.type ? ` (${r.type})` : ""}`}
                      </div>
                    </div>
                    <div className="text-xs text-neutral-500">
                      Submitted: {fmtDate(submittedAt)}
                    </div>
                  </div>

                  <DocRow thumbs={thumbs} labels={labels} />

                  <ReviewBox
                    onApprove={async (note) => {
                      if (tab === "id") {
                        await reviewIdVerification({
                          uid: r.uid,
                          status: "verified",
                          note,
                        });
                      } else {
                        await reviewRightToList({
                          uid: r.uid,
                          status: "verified",
                          note,
                        });
                      }
                    }}
                    onReject={async (note) => {
                      if (!window.confirm("Reject this verification? The host will see your note.")) return;
                      if (tab === "id") {
                        await reviewIdVerification({
                          uid: r.uid,
                          status: "rejected",
                          note,
                        });
                      } else {
                        await reviewRightToList({
                          uid: r.uid,
                          status: "rejected",
                          note,
                        });
                      }
                    }}
                  />
                </Card>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

/** -------------------------- mappers / helpers -------------------------- **/

// Your watchers flatten to { uid, status, submittedAt, note, urls, (type?) }.
// But you might still have older docs that used {idVerification.files.*} or nested objects.
// These helpers normalize both shapes so the UI is resilient.

function mapIdThumbs(row) {
  const urls =
    row?.urls ||
    row?.idVerification?.urls ||
    row?.idVerification?.files ||
    {};
  const front = urls.front || urls.idFrontUrl || urls.frontUrl || null;
  const back = urls.back || urls.idBackUrl || urls.backUrl || null;
  const selfie = urls.selfie || urls.selfieUrl || null;

  return {
    thumbs: [front, back, selfie].filter(Boolean),
    labels: ["ID front", "ID back", "Selfie"],
  };
}

function mapRtlThumbs(row) {
  const urls =
    row?.urls ||
    row?.rightToList?.urls ||
    row?.rightToList?.files ||
    {};
  const doc = urls.doc || urls.docUrl || null;
  const consent = urls.consent || urls.consentUrl || urls.leaseUrl || null;

  const isTenant = (row?.type || row?.rightToList?.type) === "tenant";
  const primaryLabel = isTenant ? "Landlord consent" : "Primary doc";
  const secondaryLabel = isTenant ? "Lease page" : "Optional";

  return {
    thumbs: [doc, consent].filter(Boolean),
    labels: [primaryLabel, secondaryLabel],
  };
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-3 py-1.5 text-sm ${
        active ? "bg-black text-white" : "border border-neutral-300 hover:border-neutral-400"
      }`}
    >
      {children}
    </button>
  );
}

function Card({ children }) {
  return <div className="rounded-2xl border border-neutral-200 bg-white p-4">{children}</div>;
}

function DocRow({ thumbs = [], labels = [] }) {
  const hasDocs = (thumbs || []).length > 0;
  if (!hasDocs) {
    return <div className="mt-3 text-xs text-neutral-500">No files attached</div>;
  }

  return (
    <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
      {thumbs.map((url, idx) => (
        <DocThumb key={`${url}-${idx}`} url={url} label={labels[idx] || "Document"} />
      ))}
    </div>
  );
}

function DocThumb({ url, label }) {
  const [imgOk, setImgOk] = useState(true);

  // If the URL isn't an image or an <img> fails to load, we show an "Open document" link.
  const imageLike = useMemo(() => isProbablyImage(url), [url]);

  return (
    <div className="rounded-xl border border-neutral-200 p-2">
      <div className="mb-1 text-xs text-neutral-500">{label}</div>

      {imageLike && imgOk ? (
        <a href={url} target="_blank" rel="noreferrer" title="Open full size">
          <img
            src={url}
            alt="doc"
            className="h-40 w-full rounded-lg object-cover"
            loading="lazy"
            onError={() => setImgOk(false)}
          />
        </a>
      ) : (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs underline"
          title="Open document"
        >
          Open document
          <span className="text-neutral-400">↗</span>
        </a>
      )}
    </div>
  );
}

function ReviewBox({ onApprove, onReject }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const safeRun = useCallback(async (fn) => {
    setBusy(true);
    try {
      await fn();
      setNote("");
    } finally {
      setBusy(false);
    }
  }, []);

  return (
    <div className="mt-4 grid gap-2">
      <textarea
        rows={2}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        className="w-full rounded-xl border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-400"
        placeholder="Optional note (visible to host)"
      />
      <div className="flex items-center gap-2">
        <button
          disabled={busy}
          onClick={() => safeRun(() => onApprove(note))}
          className="rounded-full bg-emerald-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
        >
          Approve
        </button>
        <button
          disabled={busy}
          onClick={() => safeRun(() => onReject(note))}
          className="rounded-full bg-red-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
        >
          Reject
        </button>
      </div>
    </div>
  );
}

/** ------------------------------- utils -------------------------------- **/

function isProbablyImage(url) {
  // Heuristic: common image extensions OR firebase URLs with contentType hints
  // Works even when token/querystring is appended.
  return /\.(png|jpe?g|webp|gif)(\?|$)/i.test(url);
}

function fmtDate(ts) {
  try {
    if (!ts) return "—";
    const d =
      typeof ts?.toDate === "function"
        ? ts.toDate()
        : typeof ts === "number"
        ? new Date(ts)
        : new Date(ts);
    return d.toLocaleString();
  } catch {
    return "—";
  }
}
